import { BYOBusinessOffering, ByoSelectionItem, BYOSelectionState } from './useBYOSelections';
import { BYOItem, BYOStepId } from 'api/contentful/contentTypes.cloverdotcom';
import { BYOStepOrder } from 'features/build-your-own/BuildYourOwnModule';
import { getContentTypeFromContentfulProductsOrSoftware } from 'features/build-your-own/hooks/utils';
import { IProduct } from 'services/dcs/bundle';
import { BooleanFilter } from 'utils/TypeUtils';

type BYOActionType = 'ADD' | 'REMOVE' | 'REMOVE_ALL';

export type BYOSelectionAction = {
  type: BYOActionType;
  stepId: BYOStepId;
  item?: BYOItem;
};

export const BYO_SELECTION_STATE_KEY = 'BYO-SELECTED-ITEMS';

type BYOItemWithItemKeyDefined = BYOItem & { itemKey: string };

/**
 * Returns whether the step allows multiple quantities of the same item
 *
 * @param stepId: stepId to check
 * @returns true if the step allows multiple quantities of the same item
 */
export const isMultipleQuantityAllowed = (stepId?: BYOStepId) =>
  !!stepId && [BYOStepId.DEVICES, BYOStepId.ACCESSORIES].includes(stepId);

/**
 * Returns a singular step's state with only the new item
 *
 * @param item: item to be added
 * @param quantity: quantity of the item to set
 * @returns new step state
 */
const onlyNewItem = (
  {
    itemKey,
    products = [],
    supportedSoftwarePlans = [],
    headlineTitle,
    secondaryTitle,
  }: BYOItemWithItemKeyDefined,
  quantity: number,
): Record<string, ByoSelectionItem> => {
  const dcsIds = products.map(({ dcsId }) => dcsId).filter(BooleanFilter);
  const supportedSoftwarePlanDcsIds = supportedSoftwarePlans.map(({ dcsId }) => dcsId).filter(BooleanFilter);

  return {
    [itemKey]: {
      itemKey,
      quantity,
      dcsIds,
      supportedSoftwarePlanDcsIds,
      headlineTitle: headlineTitle || '',
      secondaryTitle: secondaryTitle || '',
      contentType: getContentTypeFromContentfulProductsOrSoftware(products),
    },
  };
};

/**
 * Returns a singular step's state with the new item added to existing items
 *
 * @param existingItems: existing items in the step selection state
 * @param item: item to be added
 * @param quantity: quantity of the item to set
 * @returns new step state
 */
const existingItemsPlusNewItem = (
  existingItems: Record<string, ByoSelectionItem>,
  item: BYOItemWithItemKeyDefined,
  quantity: number,
): Record<string, ByoSelectionItem> => ({
  ...existingItems,
  ...onlyNewItem(item, quantity),
});

/**
 * Returns the subsect of BYO selection state for all future steps with all selections cleared
 *
 * @param state: current BYO selection state
 * @param stepId: current stepId being updated
 * @returns subsect of BYO selection state for all future steps with selections cleared
 */
const getFutureStepsInitialState = (stepId: BYOStepId): { [K in keyof typeof BYOStepId]?: never } => {
  const futureStepIds = BYOStepOrder.slice(BYOStepOrder.indexOf(stepId) + 1);

  return futureStepIds.reduce(
    (stateSubsect, futureStepId) => ({
      ...stateSubsect,
      [futureStepId]: {},
    }),
    {},
  );
};

/**
 * Returns new state after adding an item to the BYO selection
 *
 * @param state: current BYO selection state
 * @param stepId: stepId to be updated
 * @param item: item to be updated
 * @returns new state
 */
const getStateAfterAdd = (state: BYOSelectionState, stepId: BYOStepId, item: BYOItemWithItemKeyDefined) => {
  const isItemAlreadySelected = !!state[stepId][item.itemKey];
  const shouldDeselectOtherItems = stepId === BYOStepId.POS_NEEDS;

  const newQuantity =
    isItemAlreadySelected && isMultipleQuantityAllowed(stepId) ? state[stepId][item.itemKey].quantity + 1 : 1;

  // Check if stepId is ALL_COMPATIBLE_ACCESSORIES
  const shouldClearState = stepId !== BYOStepId.ALL_COMPATIBLE_ACCESSORIES;

  // Apply getFutureStepsInitialState if shouldClearState is true
  const futureStepsInitialState = shouldClearState ? getFutureStepsInitialState(stepId) : {};

  return {
    ...state,
    ...futureStepsInitialState,
    [stepId]: shouldDeselectOtherItems
      ? onlyNewItem(item, newQuantity)
      : existingItemsPlusNewItem(state[stepId], item, newQuantity),
  };
};

/**
 * Returns new state after removing an item from the BYO selection
 *
 * @param state: current BYO selection state
 * @param stepId: stepId to be updated
 * @param item: item to be updated
 * @returns new state
 */
const getStateAfterRemove = (
  state: BYOSelectionState,
  stepId: BYOStepId,
  item: BYOItemWithItemKeyDefined,
) => {
  // Decrement when item has multiple quantity
  const hasMultipleQuantity = state[stepId][item.itemKey].quantity > 1;
  if (hasMultipleQuantity) {
    const newQuantity = state[stepId][item.itemKey].quantity - 1;
    return {
      ...state,
      ...getFutureStepsInitialState(stepId),
      [stepId]: existingItemsPlusNewItem(state[stepId], item, newQuantity),
    };
  }

  // Delete item from state when quantity is <= 1
  const stateCopy = { ...state };
  delete stateCopy[stepId][item.itemKey];
  return {
    ...stateCopy,
    ...getFutureStepsInitialState(stepId),
  };
};

const getStateAfterRemoveAll = (
  state: BYOSelectionState,
  stepId: BYOStepId,
  item: BYOItemWithItemKeyDefined,
) => {
  const stateCopy = { ...state };
  delete stateCopy[stepId][item.itemKey];

  return {
    ...stateCopy,
    [stepId]: {},
  };
};

/**
 * Reducer which returns new Build-Your-Own (BYO) selectionState based on action
 *
 * @param state: current BYO selection state
 * @param {BYOActionType} action.type: action type
 * @param {BYOStepId} action.stepId: stepId to be updated
 * @param {BYOItem} action.item: item to be updated
 * @returns new state
 */
export const selectionReducer = (state: BYOSelectionState, { type, stepId, item }: BYOSelectionAction) => {
  if (!item?.itemKey && type !== 'REMOVE_ALL') return state;

  const itemWithItemKey = item as BYOItemWithItemKeyDefined;

  switch (type) {
    case 'ADD':
      return getStateAfterAdd(state, stepId, itemWithItemKey);
    case 'REMOVE':
      return getStateAfterRemove(state, stepId, itemWithItemKey);
    case 'REMOVE_ALL':
      return getStateAfterRemoveAll(state, stepId, itemWithItemKey);
  }
};

/**
 * Returns filtered items based on the business offering selection's supported software plans
 * Follows the BYO Miro relationships: https://miro.com/app/board/uXjVP59HluU=/
 *
 * @param businessOffering - Business offering selection item key
 * @param posNeedsItems - Items to be filtered
 * @param previousStepSelections - Selection state from the previous step (i.e., BUSINESS_OFFERING step)
 * @param overrideSupportedSoftwarePlanDcsIds - Optional override for the supported software plan DcsIds
 * @returns Filtered items based on the business offering selection's supported software plans or via the override
 */
export const filteredPosNeedsForBusinessOffering = (
  businessOffering: BYOBusinessOffering,
  posNeedsItems: BYOItem[],
  previousStepSelections: Record<BYOBusinessOffering, ByoSelectionItem>,
  overrideSupportedSoftwarePlanDcsIds?: string[],
): BYOItem[] => {
  // Determine the list of supported DcsIds based on the business offering
  const supportedDcsIds = getSupportedDcsIds(
    businessOffering,
    previousStepSelections,
    overrideSupportedSoftwarePlanDcsIds,
  );

  // Filter the posNeedsItems based on whether all their products are supported
  return posNeedsItems.filter(posNeedsItem => isPosNeedsItemSupported(posNeedsItem, supportedDcsIds));
};

/**
 * Determines the list of supported DcsIds based on the business offering and any overrides
 *
 * @param businessOffering - The current business offering
 * @param previousStepSelections - Selections from the previous step
 * @param overrideSupportedSoftwarePlanDcsIds - Optional override DcsIds
 * @returns An array of supported DcsIds
 */
function getSupportedDcsIds(
  businessOffering: BYOBusinessOffering,
  previousStepSelections: Record<BYOBusinessOffering, ByoSelectionItem>,
  overrideSupportedSoftwarePlanDcsIds?: string[],
): string[] {
  if (overrideSupportedSoftwarePlanDcsIds) {
    return overrideSupportedSoftwarePlanDcsIds;
  }

  if (businessOffering === BYOBusinessOffering.RETAIL_AND_SERVICES) {
    const supportedRetailIds = previousStepSelections[BYOBusinessOffering.RETAIL].supportedSoftwarePlanDcsIds;
    const supportedServicesIds =
      previousStepSelections[BYOBusinessOffering.SERVICES].supportedSoftwarePlanDcsIds;

    // Combine and deduplicate the IDs
    return Array.from(new Set([...supportedRetailIds, ...supportedServicesIds]));
  }

  return previousStepSelections[businessOffering].supportedSoftwarePlanDcsIds;
}

/**
 * Checks if all products in a posNeedsItem are supported based on the provided DcsIds
 *
 * @param posNeedsItem - The item to check
 * @param supportedDcsIds - The list of supported DcsIds
 * @returns True if the item is supported, false otherwise
 */
export function isPosNeedsItemSupported(posNeedsItem: BYOItem, supportedDcsIds: string[]): boolean {
  const products = posNeedsItem.products;
  if (!products || products.length === 0) {
    // If there are no products, consider the item unsupported
    return false;
  }

  // Check that every product's dcsId is included in the list of supported DcsIds
  return products.every(product => {
    const dcsId = product.dcsId || '';
    return supportedDcsIds.includes(dcsId);
  });
}

const isItemForceShownForSoftwarePlan = (item: BYOItem, softwarePlanDcsId: string) =>
  item.forceShowForSoftwarePlans?.some(softwarePlan => softwarePlan.dcsId === softwarePlanDcsId);

/**
 * Returns true when first product in the item is supported by the software plan, and item is not a combo-item
 *
 * @param item: byo item
 * @param softwarePlanDcsId: dcsId of the software plan
 * @param dcsProducts: dcs products
 * @returns true if first product in the item is supported by the software plan & item is not a combo-item, false otherwise
 */
const isNotComboItemAndFirstProductSupportedBySoftwarePlan = (
  deviceItem: BYOItem,
  softwarePlanDcsId: string,
  dcsProducts: IProduct[],
) => {
  const products = deviceItem.products || [];
  if (products.length !== 1) return false;

  const firstProduct = products[0];
  const deviceDcsId = firstProduct.dcsId;
  const dcsProduct = dcsProducts.find(p => p.id === deviceDcsId);

  if (!dcsProduct || !dcsProduct.supportedSoftwarePlans) {
    return false;
  }

  return dcsProduct.supportedSoftwarePlans.some(s => s.id === softwarePlanDcsId);
};

/**
 * Returns filtered items based on the software plan selection's supported devices
 * Follows the BYO relationships: https://docs.google.com/spreadsheets/d/1UnKrpj9FVj0qXotzNViD6AcBjvfXgngkJGaiLposqJk/edit?usp=sharing
 *
 * @param softwarePlanDcsId: dcsId of the software plan
 * @param deviceItems: device items to be filtered
 * @param dcsProducts: array of DCS products
 * @returns filtered items based on the software plan selection's supported devices
 */
export const filteredDevicesForSoftwarePlan = (
  softwarePlanDcsId: string,
  deviceItems: BYOItem[],
  dcsProducts: IProduct[],
) =>
  deviceItems.filter(
    deviceItem =>
      isItemForceShownForSoftwarePlan(deviceItem, softwarePlanDcsId) ||
      isNotComboItemAndFirstProductSupportedBySoftwarePlan(deviceItem, softwarePlanDcsId, dcsProducts),
  );

export const getDCSAccessoriesForDCSIds = (dcsIds: string[], dcsProducts: IProduct[]) =>
  dcsIds.reduce((acc, dcsId) => {
    const dcsProduct = dcsProducts.find(p => p.id === dcsId);
    if (!dcsProduct) {
      return acc;
    }
    return [...acc, ...(dcsProduct.accessories || [])];
  }, []);

/**
 * Returns the DCS ids of the supported accessories for the given devices
 *
 * @param deviceSelectionItems: array of BYO selection items selected in devices step
 * @param dcsProducts: array of DCS products
 * @returns array of DCS ids of the supported accessories for the given devices
 */
export const getSupportedAccessoriesDCSIds = (
  deviceSelectionItems: ByoSelectionItem[],
  dcsProducts: IProduct[],
) =>
  deviceSelectionItems.reduce((acc, deviceSelectionItem) => {
    const dcsAccessories = getDCSAccessoriesForDCSIds(deviceSelectionItem.dcsIds, dcsProducts);

    const dcsAccessoriesIds = dcsAccessories.map(d => d.id);
    return [...acc, ...dcsAccessoriesIds];
  }, []);

/**
 * Returns filtered accessories based on the supported accessories for the given devices selected
 * and the software plan selected
 * Follows the BYO compatibility relationships: https://docs.google.com/spreadsheets/d/1fp9LIgCozER5ukYSrz974LX0QHNjE_WssjSOdfNmQPw/edit#gid=0
 *
 * @param softwarePlanDcsId: dcsId of the software plan
 * @param deviceSelectionItems: array of ByoSelectionItems selected in devices step
 * @param accessoryItems: array of all BYO items for accessories step
 * @param dcsProducts: array of all DCS products
 * @returns filtered accessories based on the supported accessories for the given devices selected
 */
export const filteredAccessoriesForDevicesAndSoftwarePlan = (
  softwarePlanDcsId: string,
  deviceSelectionItems: ByoSelectionItem[],
  accessoryItems: BYOItem[],
  dcsProducts: IProduct[],
) => {
  const supportedAccessoriesDcsIds = getSupportedAccessoriesDCSIds(deviceSelectionItems, dcsProducts);

  return accessoryItems.filter(accessoryItem => {
    const accessoryDcsId = accessoryItem.products?.[0].dcsId;
    return (
      supportedAccessoriesDcsIds.includes(accessoryDcsId || '') &&
      isNotComboItemAndFirstProductSupportedBySoftwarePlan(accessoryItem, softwarePlanDcsId, dcsProducts)
    );
  });
};

/**
 * If user has a selected software plan, user must select at least one device to proceed
 *
 * If user has no selected softwareplan, return false
 *
 * @param selectionState: item(s) in selected state
 * @param numItemsSelected: the number of BYO items selected for devices step
 * @returns true if step is completed, false otherwise
 */
export const isDevicesStepComplete = (selectionState: BYOSelectionState, numItemsSelected: number) => {
  const posNeedsStepSelection = Object.keys(selectionState[BYOStepId.POS_NEEDS])[0];

  if (!posNeedsStepSelection) {
    return false;
  }

  return numItemsSelected > 0;
};

export const getAllProductDcsIdsWithQuantities = (
  getDcsIdsToQuantity: (stepId: BYOStepId) => { dcsId: string; quantity: number }[],
) => {
  const devicesDcsIdToQuantity = getDcsIdsToQuantity(BYOStepId.DEVICES);
  const accessoriesDcsIdToQuantity = getDcsIdsToQuantity(BYOStepId.ACCESSORIES);
  return [...devicesDcsIdToQuantity, ...accessoriesDcsIdToQuantity];
};
